/**
 * Support at Home — Digital Statement of Advice.
 *
 * The SAH twin of AgedCareSuggestionEngineDigitalSOAAttachment.tsx, deliberately mirroring its
 * shape: a flat, serialisable SNAPSHOT built once (buildSupportAtHomeDigitalSOASnapshot), a pure
 * HTML renderer over it (buildHtml — exported for tests), and open/download plumbing
 * (openSupportAtHomeDigitalSOAAttachment). The snapshot is persisted onto the saved strategy
 * (`soaSnapshot`) so the document can be reopened exactly as generated.
 *
 * Two structural differences from the aged care version, both deliberate:
 *  - it is built from a SAVED home-care strategy (either mode), not live screen state — the
 *    document always matches something on file;
 *  - the strategy narrative (what it is / benefits / risks / suitability) comes from
 *    sahStrategyExplainers.ts, the same library the insight panel renders — screen and SOA can
 *    never tell the client different stories.
 *
 * RAD/room-price sections are replaced by what matters for home care: the contribution-rate
 * assessment (including any Services Australia assessment-letter override, disclosed with its
 * provenance) and the service plan the modelling used.
 */
import * as calculationApi from '../../services/calculationApi.ts';
import { LIFETIME_CAPS } from '../../../../shared/src/calculation/supportAtHomeCalculations.ts';
import { getCurrentAdviceAttribution } from '../professionalPortal/adviceAttribution.ts';
import { pensionCallout } from '../advice/agePensionClaim.ts';
import { formatCurrency, calculateAge } from '../../utils/index.ts';
import { GENERAL_TRANSFER_BALANCE_CAP } from '../../constants/index.ts';
import {
    buildDigitalSoaStructureAppendix,
    buildDigitalSoaStructureAppendixHtml,
} from '../../utils/digitalSoaStructureAppendix.ts';
import { ratesReviewStatus } from '../../../../shared/src/calculation/ratesReview.ts';
import { projectionPeriodConventionSentence } from '../../../../shared/src/calculation/projectionPeriodDescription.ts';
import { SAH_STRATEGY_EXPLAINERS } from './sahStrategyExplainers.ts';
import { buildStrategyRisks, riskKindsFromSupportAtHomeType, type StrategyRisks } from '../../strategy/strategyRisks.ts';
import { buildFactFindSections } from '../agedCare/AgedCareSuggestionEngineDigitalSOAAttachment.tsx';
import { buildAssumptionSections } from '../advice/assumptionSections.ts';
import { SOA_THEME_CSS, situationFrom, renderSituationHtml, renderProjectionCharts, renderAssumptionsAppendixHtml } from '../advice/soaKit.ts';
import { SUPPORT_AT_HOME_SERVICES } from './supportAtHomeServices.tsx';
import type { SupportAtHomeServicePlan } from '../../strategy/supportAtHomeEstimatedServicePlan.ts';
import type { SavedStrategy } from '../../strategy/StrategyIntegrationUtils.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { assetAssumptionFiguresFor } from '../../../../shared/src/calculation/assetAssumptionRows.ts';
import { stockPhoto } from '../../assets/stock/index.ts';

// Served as a same-origin <script src> in the popup HTML rather than inlined, so the popup doesn't
// need 'unsafe-inline' in script-src — same pattern (and same runtime) as the aged care SOA.
const soaBookletScriptUrl = new URL('../agedCare/soaBookletScript.ts', import.meta.url).href;

// ── small helpers (mirroring the aged care attachment's private ones) ───────────────────────────

function toNumber(value: unknown): number {
    const parsed = parseFloat(String(value));
    return Number.isFinite(parsed) ? parsed : 0;
}

function sanitizeText(value: unknown): string {
    return String(value || '')
        .replace(/Ã¢â‚¬â€œ|â€“/g, '-')
        .replace(/Ã¢â‚¬â€”|â€”/g, '-')
        .replace(/Ã¢â‚¬â„¢|â€™/g, "'")
        .replace(/Ã¢â‚¬Å“|Ã¢â‚¬Â|â€œ|â€/g, '"')
        .replace(/Ã‚/g, '')
        .trim();
}

function formatDateOnly(dateValue: string | number | Date): string {
    return new Date(dateValue).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: '2-digit' });
}

function pct(value: unknown): string {
    return `${((Number(value) || 0) * 100).toFixed(1)}%`;
}

/** Default income/growth rates per asset type — mirrors the aged care SOA's appendix table. */

/** A field key as a label: `home_care_for` and `annuityAppetite` both read "Home Care For" / "Annuity Appetite". */
function prettify(text: unknown): string {
    const words = String(text || '').replace(/([A-Z])/g, ' $1').split(/[_\s]+/).filter(Boolean);
    return sanitizeText(words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '));
}

/**
 * A stored value as the appendix prints it. Booleans are Yes/No; a stored word or code is shown in
 * sentence case with its underscores as spaces ("both" → "Both", "own_home" → "Own home"), so every
 * row reads the same way (owner, 15 Oct 2026: "Yes" beside "yes" in the one section).
 */
export function toDisplay(value: unknown): string {
    if (value === null || value === undefined || value === '') return 'Not provided';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    const text = String(value);
    if (/^[a-z][a-z0-9_ ]*$/i.test(text) && !/^\d/.test(text)) {
        const spaced = text.replace(/_/g, ' ');
        return sanitizeText(spaced.charAt(0).toUpperCase() + spaced.slice(1));
    }
    return sanitizeText(text);
}

function flattenObject(value: unknown, prefix = ''): Array<{ key: string; value: string }> {
    if (value === null || value === undefined) return [];
    if (Array.isArray(value)) return value.flatMap((item, idx) => flattenObject(item, `${prefix}[${idx}]`));
    if (typeof value === 'object') {
        return Object.entries(value as Record<string, unknown>).flatMap(([key, nested]) => flattenObject(nested, prefix ? `${prefix}.${key}` : key));
    }
    return [{ key: prefix, value: String(value) }];
}

const GOAL_LABELS: Record<string, string> = {
    home_care: 'Access home care support',
    aged_care: 'Move into residential aged care',
    downsize_home: 'Downsize the family home',
    retirement_village: 'Move to a retirement village',
    granny_flat: 'Explore granny flat arrangements',
    meet_costs: 'Meet care costs',
    maximise_support: 'Maximise government support',
    preserve_assets: 'Preserve assets for inheritance',
    funding_guidance: 'Guidance on funding options',
};

// ── service plan rows ───────────────────────────────────────────────────────────────────────────

interface SoaServicePlanRow {
    name: string;
    detail: string;
    annual: number;
}

function servicePlanSideRows(side: Record<string, { quantity?: number; frequency?: string } | undefined> | undefined): SoaServicePlanRow[] {
    const rows: SoaServicePlanRow[] = [];
    if (!side) return rows;
    const catalogue = Object.values(SUPPORT_AT_HOME_SERVICES).flatMap((c) => c.services.map((s) => ({ ...s, category: c.label })));
    Object.entries(side).forEach(([serviceId, allocation]) => {
        const quantity = Number(allocation?.quantity) || 0;
        if (quantity <= 0) return;
        const frequency = allocation?.frequency || 'monthly';
        const entry = catalogue.find((s) => s.id === serviceId);
        // Same month-count convention as the cost engine: daily ×30, weekly ×4, monthly ×1.
        const perMonth = frequency === 'daily' ? quantity * 30 : frequency === 'weekly' ? quantity * 4 : quantity;
        rows.push({
            name: entry?.name || serviceId,
            detail: `${quantity}/${frequency === 'daily' ? 'day' : frequency === 'weekly' ? 'week' : 'month'} · ${entry?.category || ''}`,
            annual: (entry?.medianPrice || 0) * perMonth * 12,
        });
    });
    return rows.sort((a, b) => b.annual - a.annual);
}

// ── which narratives apply to a saved strategy ──────────────────────────────────────────────────

/** A suggestion-engine save carries one strategy type; a manual save can combine several levers.
 * Derives the ordered list of narrative keys this document must explain. */
export function narrativeTypesForSavedStrategy(strategy: SavedStrategy): string[] {
    const inputs = (strategy?.inputs || {}) as Record<string, unknown>;
    const seType = (inputs.seConfig as { type?: string } | undefined)?.type;
    if (seType) return SAH_STRATEGY_EXPLAINERS[seType] ? [seType] : [];

    const types: string[] = [];
    if (toNumber(inputs.gifting) > 0) types.push('gifting');
    if (toNumber(inputs.funeralBonds) > 0) types.push('funeralBonds');
    const property = String(inputs.propertyStrategy || 'none');
    if (property !== 'none' && SAH_STRATEGY_EXPLAINERS[property]) types.push(property);
    if (inputs.selectedFundingStrategy === 'annuity' && inputs.annuityRecord) types.push('annuity');
    const overall = (inputs.overallStrategy || {}) as Record<string, unknown>;
    if (overall.reverseMortgage && !types.includes('reverseMortgage')) types.push('reverseMortgage');
    if (overall.heas && !types.includes('heas')) types.push('heas');
    return types;
}

// ── snapshot ────────────────────────────────────────────────────────────────────────────────────

interface SoaProjectionRowLike {
    fyLabel?: string;
    year?: number | string;
    socialSecurity?: number;
    investmentIncome?: number;
    annualCareCost?: number;
    annualCareCostForPartialPeriod?: number;
    cashFlow?: number;
    cashflow?: number;
    netWorth?: number;
    [key: string]: unknown;
}

interface SoaAlternativeLike {
    name?: string;
    config?: { type?: string };
    projectedEstateValue?: number;
    snapshot?: { pension?: number; netCashflow?: number; cashflow?: number; legacyValue?: number; annualContribution?: number; totalSupportAtHomeCosts?: number };
    [key: string]: unknown;
}

export interface BuildSupportAtHomeDigitalSOASnapshotParams {
    selectedStrategy: SavedStrategy;
    factFind: DraftFactFind;
    /** Engine projection rows for the SELECTED strategy (saved home-care strategies don't carry
     * them, so the caller passes what it has at save time). */
    projections?: SoaProjectionRowLike[] | null;
    /** The SE analysis payload when generated from the suggestion engine — drives "alternatives
     * considered". */
    analysis?: { strategies?: SoaAlternativeLike[]; totalEvaluated?: number; totalGenerated?: number } | null;
    /** The service plan the modelling used (resolved by the caller — user plan or null for the
     * illustrative package). */
    servicePlan?: SupportAtHomeServicePlan | null;
    servicePlanIsEstimated?: boolean;
    servicePlanDescription?: string | null;
    strategySource: 'suggestion_engine' | 'manual_control';
}

export async function buildSupportAtHomeDigitalSOASnapshot(params: BuildSupportAtHomeDigitalSOASnapshotParams) {
    const { selectedStrategy, factFind } = params;
    if (!selectedStrategy || !factFind) return null;

    // Baseline (do nothing) position, fetched fresh — the same discipline as the aged care builder.
    const baselineFinancials = await calculationApi.calculateFinancials(factFind as never) as Record<string, unknown>;
    const baselineContributions = await calculationApi.calculateIndividualContributions(
        factFind as never, baselineFinancials as never, 'standard', null, true, 0, 0.5, {}
    ) as Record<string, any>;
    const isCouple = factFind?.adviceFor === 'couple';
    const baselineBranch = (isCouple ? baselineContributions?.combined : baselineContributions?.client) || {};

    const outputs = (selectedStrategy.outputs || {}) as Record<string, any>;
    const strategyRates = (outputs.contributionRates || baselineBranch.contributionRates || {}) as Record<string, number>;

    const override = (factFind as { sahContributionRateOverride?: Record<string, unknown> }).sahContributionRateOverride || null;

    const clientRows = servicePlanSideRows(params.servicePlan?.client);
    const partnerRows = isCouple ? servicePlanSideRows(params.servicePlan?.partner) : [];

    const narrativeTypes = narrativeTypesForSavedStrategy(selectedStrategy);

    const projections = (params.projections || []).map((row, index) => ({
        label: sanitizeText(row.fyLabel || `Year ${index + 1}`),
        pension: toNumber(row.socialSecurity),
        sahCost: toNumber(row.annualCareCostForPartialPeriod ?? row.annualCareCost),
        cashflow: toNumber(row.cashFlow ?? row.cashflow),
        netWorth: toNumber(row.netWorth),
    }));

    const alternatives = (params.analysis?.strategies || []).map((s) => ({
        name: sanitizeText(s.name || 'Strategy'),
        type: String(s.config?.type || ''),
        legacy: toNumber(s.projectedEstateValue ?? s.snapshot?.legacyValue),
        cashflow: toNumber(s.snapshot?.netCashflow ?? s.snapshot?.cashflow),
        pension: toNumber(s.snapshot?.pension),
        selected: sanitizeText(s.name || '') === sanitizeText(selectedStrategy.name || ''),
    }));

    const structureAppendix = buildDigitalSoaStructureAppendix(factFind as never, { strategySource: params.strategySource });

    // The do-nothing legacy, when the analysis carried it — the exec summary's estate comparison.
    const doNothingAlt = (params.analysis?.strategies || []).find((s) => s.config?.type === 'doNothing');
    const baselineLegacy = doNothingAlt ? toNumber(doNothingAlt.projectedEstateValue ?? doNothingAlt.snapshot?.legacyValue) : null;

    // After-strategy assessable position, from the saved strategy's own financials when carried.
    const outputsFinancials = (outputs.financials || {}) as Record<string, unknown>;

    const assetRows = Object.entries((factFind?.assets || {}) as Record<string, Array<Record<string, unknown>> | undefined>).flatMap(([assetType, assets]) =>
        (assets || []).map((asset) => ({
            assetType: prettify(assetType),
            description: sanitizeText(asset.description || 'Unnamed Asset'),
            value: toNumber(asset.value),
            // The figures the MODEL used — for a property that is the recorded rent and expenses,
            // never an assumed yield. See shared/src/calculation/assetAssumptionRows.ts.
            ...assetAssumptionFiguresFor(assetType, asset),
        }))
    );

    // Only the ticked goals: the goals object also carries settings (legacyYears, dzMotive, …).
    const goals = Object.entries((factFind?.goals || {}) as Record<string, unknown>)
        .filter(([key, on]) => on === true && !key.endsWith('_for'))
        .map(([key]) => GOAL_LABELS[key] || prettify(key));

    return {
        documentKind: 'support_at_home' as const,
        reportId: `SAH-${Date.now().toString(36).toUpperCase()}`,
        generatedAt: new Date().toISOString(),
        strategySource: params.strategySource,
        clientName: sanitizeText(factFind?.client?.firstName || 'Client'),
        partnerName: isCouple ? sanitizeText(factFind?.partner?.firstName || '') : '',
        clientAge: factFind?.client?.dob ? calculateAge(factFind.client.dob) : null,
        completerDisplay: sanitizeText(factFind?.completerFirstName || ''),
        goals,
        selectedStrategyName: sanitizeText(selectedStrategy.name || 'Selected strategy'),
        narrativeTypes,
        // The same "What could go wrong" list the card shows (strategyRisks.ts), so the document and
        // the screen cannot disagree about the risks of the chosen strategy.
        strategyRisks: buildStrategyRisks(
            narrativeTypes.flatMap((type) => riskKindsFromSupportAtHomeType(type, String(selectedStrategy.name || ''))),
        ) as StrategyRisks,
        // Disclose when the modelling was run against a LINKED aged care scenario (one partner
        // modelled in residential care — illness-separated pension treatment may apply).
        agedCareLinked: ((selectedStrategy.inputs || {}) as { agedCareContext?: string }).agedCareContext === 'linked',

        // Headline outcomes — from the SAVED strategy's own outputs.
        annualPension: toNumber(outputs.agePension),
        annualContribution: toNumber(outputs.annualContribution),
        annualCashflow: toNumber(outputs.cashFlow),
        projectedLegacy: toNumber(outputs.legacyEstimate),

        baseline: {
            pension: toNumber(baselineFinancials.socialSecurity),
            // Net of the baseline contribution when the strategy's own cashflow is: StrategiesSection
            // saves the panel's figure, which nets the service plan's share the way the Service
            // Builder does, and says so in `cashFlowBasis`. A strategy saved before that carries the
            // gross figure and is compared gross — never net against gross.
            cashflow: toNumber(baselineFinancials.cashFlow)
                - (outputs.cashFlowBasis === 'net_of_service_plan' ? toNumber(baselineBranch?.contributionAmounts?.totalAnnual) : 0),
            assessableAssets: toNumber(baselineFinancials.assessableAssets),
            assessableIncome: toNumber(baselineFinancials.totalAssessableIncome),
            pensionStatus: String(baselineBranch.pensionStatus || ''),
            contribution: toNumber(baselineBranch?.contributionAmounts?.totalAnnual),
            contributionRates: {
                clinical: toNumber(baselineBranch?.contributionRates?.clinical),
                independence: toNumber(baselineBranch?.contributionRates?.independence),
                everydayLiving: toNumber(baselineBranch?.contributionRates?.everydayLiving),
            },
            legacy: baselineLegacy,
        },
        // The means-test machinery behind the rates — drives the "show the workings" expandable.
        thresholds: {
            incomeMin: toNumber(baselineBranch?.thresholds?.incomeMin),
            incomeMax: toNumber(baselineBranch?.thresholds?.incomeMax),
            assetMin: toNumber(baselineBranch?.thresholds?.assetMin),
            assetMax: toNumber(baselineBranch?.thresholds?.assetMax),
        },
        afterStrategy: {
            assessableAssets: toNumber(outputsFinancials.assessableAssets),
            assessableIncome: toNumber(outputsFinancials.totalAssessableIncome),
        },

        contributionRates: {
            clinical: toNumber(strategyRates.clinical),
            independence: toNumber(strategyRates.independence),
            everydayLiving: toNumber(strategyRates.everydayLiving),
        },
        // Whether the Department's 'no worse off' principle covers the person receiving services (a Home
        // Care Package on 12 Sep 2024) — read from the profile, the same answer the contribution engine uses.
        noWorseOff: (() => {
            const acat = (factFind as { careNeeds?: { clientAcat?: { hcpBeforeSep2024?: string; hcpCategory?: string; previousITCF?: number } } })?.careNeeds?.clientAcat;
            if (acat?.hcpBeforeSep2024 !== 'yes') return null;
            return { paidNoFee: acat.hcpCategory === 'A', previousFeeDaily: Math.max(0, Number(acat.previousITCF) || 0) };
        })(),
        rateOverride: override
            ? {
                independence: toNumber(override.independence),
                everydayLiving: toNumber(override.everydayLiving),
                source: sanitizeText(override.source),
                overriddenAt: String(override.overriddenAt || ''),
                modelledIndependence: toNumber(override.modelledIndependenceAtOverride),
                modelledEverydayLiving: toNumber(override.modelledEverydayLivingAtOverride),
            }
            : null,

        servicePlan: {
            clientRows,
            partnerRows,
            grossAnnual: [...clientRows, ...partnerRows].reduce((sum, r) => sum + r.annual, 0),
            isEstimated: Boolean(params.servicePlanIsEstimated ?? (clientRows.length === 0 && partnerRows.length === 0)),
            description: sanitizeText(params.servicePlanDescription || ''),
        },

        alternatives,
        totalEvaluated: params.analysis?.totalEvaluated ?? params.analysis?.strategies?.length ?? 0,
        projections,
        projectionConvention: sanitizeText(projectionPeriodConventionSentence('financial_year' as never) || ''),
        ratesReviewNotice: (() => {
            const status = ratesReviewStatus() as { message?: string } | null;
            return sanitizeText(status?.message || '');
        })(),
        structureAppendixHtml: structureAppendix
            ? buildDigitalSoaStructureAppendixHtml(structureAppendix, formatCurrency as never)
            : '',
        // Full input disclosure — the SAME walker as the aged care SOA, so the structured-inputs
        // appendix is identical across both documents.
        annualLivingExpenses: toNumber((factFind?.expenses as { livingExpenses?: unknown } | undefined)?.livingExpenses),
        assetRows,
        factFindSections: buildFactFindSections(factFind),
        allFactFindRows: flattenObject(factFind || {}),
        // The shared document pieces (features/advice/soaKit.ts, 3 Oct 2026): the position as
        // entered, the do-nothing run's year-by-year for the charts, and the Assumptions page as
        // it stood when the document was generated, for the appendix.
        situation: situationFrom(factFind),
        legacyYears: parseInt(String((factFind?.goals as { legacyYears?: unknown } | undefined)?.legacyYears), 10) || 5,
        baselineProjections: ((doNothingAlt?.projections || []) as SoaProjectionRowLike[]).map((row, index) => ({
            label: sanitizeText(row.fyLabel || `Year ${index + 1}`),
            pension: toNumber(row.socialSecurity),
            cashflow: toNumber(row.cashFlow ?? row.cashflow),
            netWorth: toNumber(row.netWorth),
        })),
        assumptionSections: buildAssumptionSections(),
    };
}

export type SupportAtHomeSoaSnapshot = NonNullable<Awaited<ReturnType<typeof buildSupportAtHomeDigitalSOASnapshot>>>;

// ── HTML ────────────────────────────────────────────────────────────────────────────────────────

function narrativeSectionHtml(type: string): string {
    const explainer = SAH_STRATEGY_EXPLAINERS[type];
    if (!explainer) return '';
    const { soa } = explainer;
    return `
<div class="card-inset">
<p class="lede" style="margin-top:0">${soa.description}</p>
<div class="h3" style="margin-top:12px">Benefits</div>
<ul class="riskList">${soa.benefits.map((b) => `<li>${b}</li>`).join('')}</ul>
<div class="h3">Risks and trade-offs</div>
<ul class="riskList">${soa.risks.map((r) => `<li>${r}</li>`).join('')}</ul>
<div class="h3">Suitability</div>
<p>${soa.suitability}</p>
</div>`;
}

function servicePlanTableHtml(label: string, rows: SoaServicePlanRow[], showLabel: boolean): string {
    if (!rows.length) return '';
    return `
${showLabel ? `<div class="h3">${label}</div>` : ''}
<table><thead><tr><th>Service</th><th>Frequency &amp; category</th><th>Indicative gross cost</th></tr></thead>
<tbody>${rows.map((r) => `<tr><td>${r.name}</td><td>${r.detail}</td><td class="td-num">${formatCurrency(r.annual)}/yr</td></tr>`).join('')}</tbody></table>`;
}


/**
 * The 'no worse off' workings — Services Australia's four steps on income alone, or why nothing is
 * payable — in place of the income-and-assets walkthrough, which describes a calculation that is
 * not this client's. Mirrors the contributions screen (ContributionsSection.tsx).
 */
function noWorseOffWorkings(
    snapshot: {
        noWorseOff: { paidNoFee: boolean; previousFeeDaily: number } | null;
        contributionRates: { independence: number; everydayLiving: number; clinical: number };
        baseline: { pensionStatus?: string };
    },
    income: number,
    incomeMin: number,
    incomeMax: number,
): string {
    const n = snapshot.noWorseOff;
    if (!n) return '';
    const pctOf = (v: number) => `${(v * 100).toFixed(2)}%`;
    if (n.paidNoFee) {
        return `<p class="small"><strong>Why nothing is payable.</strong> No income-tested care fee was payable under the Home Care Package, so under the 'no worse off' principle no contributions are payable under Support at Home — whatever the assessable income and assets, and even after a reassessment into a higher classification.</p>`;
    }
    const status = snapshot.baseline.pensionStatus;
    const step1 = Math.round(Math.max(0, income - incomeMin) * 0.5);
    const step2 = Math.round(Math.max(0, incomeMax - incomeMin) * 0.5);
    const step3 = step2 > 0 ? Math.min(100, (step1 / step2) * 100) : 0;
    const arithmetic = status === 'fullPensioner'
        ? `<p class="small"><strong>Step 2 — the rate.</strong> As a full pensioner the 'no worse off' rate is <strong>0%</strong> on both Independence and Everyday Living.</p>`
        : status === 'selfFunded'
            ? `<p class="small"><strong>Step 2 — the rate.</strong> As a self-funded retiree the 'no worse off' rate is the maximum, <strong>25%</strong> on both Independence and Everyday Living (the standard rates would be 50% and 80%).</p>`
            : `<p class="small"><strong>Step 2 — the arithmetic (Services Australia's method).</strong> (${formatCurrency(income)} − ${formatCurrency(incomeMin)}) × 50% = ${formatCurrency(step1)}; (${formatCurrency(incomeMax)} − ${formatCurrency(incomeMin)}) × 50% = ${formatCurrency(step2)}; ${formatCurrency(step1)} ÷ ${formatCurrency(step2)} = ${step3.toFixed(2)}% of the way → × 25% = <strong>${pctOf(snapshot.contributionRates.independence)}</strong> on both Independence and Everyday Living.</p>`;
    const ceiling = n.previousFeeDaily > 0
        ? `<p class="small"><strong>Step 3 — the ceiling.</strong> Contributions never exceed the previous income-tested care fee: ${formatCurrency(n.previousFeeDaily)} a day, <strong>${formatCurrency(n.previousFeeDaily * 14)} a fortnight</strong> (${formatCurrency(n.previousFeeDaily * 14 * 26)} a year).</p>`
        : `<p class="small"><strong>Step 3 — the ceiling.</strong> Contributions never exceed the income-tested care fee paid under the package; record that fee in the profile and the tool applies the ceiling.</p>`;
    return `<p class="small"><strong>Step 1 — income only.</strong> Under the 'no worse off' principle the rate depends on assessable income alone — <strong>${formatCurrency(income)}</strong>, against the income free area (${formatCurrency(incomeMin)}) and the income limit (${formatCurrency(incomeMax)}). Assets play no part, so strategies that move assets do not change these rates.</p>
${arithmetic}
${ceiling}
<p class="small muted">Clinical Care is always ${pctOf(snapshot.contributionRates.clinical)} — fully government funded at every means level.</p>`;
}

/** Back from the snapshot's escaped text to plain words, for the kit's pieces that escape for themselves. */
function plainText(value: unknown): string {
    return String(value ?? '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
}

export function buildHtml(snapshot: SupportAtHomeSoaSnapshot): string {
    const coverA = stockPhoto('1559827260-dc66d52bef19', 1920, 1080);
    const coverB = stockPhoto('1506905925346-21bda4d32df4', 1920, 1080);
    const cashflowKpiClass = snapshot.annualCashflow < 0 ? 'kpi kpi--warn' : 'kpi kpi--gain';
    const goalsBlock = snapshot.goals.length
        ? `<ul class="goalsList">${snapshot.goals.map((g) => `<li>${g}</li>`).join('')}</ul>`
        : '<p class="muted small">No specific goals were recorded in your profile.</p>';

    const overrideBlock = snapshot.rateOverride
        ? `<div class="callout callout--amber">
<p class="callout-title">Contribution rates entered from your official assessment</p>
<p>The contribution rates in this document are the OFFICIAL rates recorded from your Services Australia assessment
— <strong>${pct(snapshot.rateOverride.independence)}</strong> Independence and <strong>${pct(snapshot.rateOverride.everydayLiving)}</strong> Everyday Living —
rather than the tool's modelled rates (${pct(snapshot.rateOverride.modelledIndependence)} / ${pct(snapshot.rateOverride.modelledEverydayLiving)} at the time they were recorded).</p>
<p class="small">Source: ${snapshot.rateOverride.source || 'not recorded'}${snapshot.rateOverride.overriddenAt ? ` · Recorded ${formatDateOnly(snapshot.rateOverride.overriddenAt)}` : ''}.
If your financial position changes materially, Services Australia may reassess these rates.</p>
</div>`
        : '';

    // The 'no worse off' principle, stated only for a client it covers — nobody else needs to read it.
    const noWorseOffBlock = snapshot.noWorseOff
        ? `<div class="callout">
<p class="callout-title">Your contribution rates are protected by the 'no worse off' principle</p>
<p>Because a Home Care Package was in place or approved for you on 12 September 2024, the Department's 'no worse off' principle applies to your Support at Home contributions.
${snapshot.noWorseOff.paidNoFee
    ? 'You were assessed as not having to pay an income-tested care fee under your package, so you pay <strong>no contributions</strong> under Support at Home — even if you are reassessed into a higher classification.'
    : `Because you paid an income-tested care fee under your package, your rates are the Department's reduced 'no worse off' rates: between 0% and 25%, based on your <strong>income only</strong> (your assets play no part), and never more per fortnight than the fee you paid before${snapshot.noWorseOff.previousFeeDaily > 0 ? ` (${formatCurrency(snapshot.noWorseOff.previousFeeDaily)} a day, ${formatCurrency(snapshot.noWorseOff.previousFeeDaily * 14)} a fortnight)` : ''}.`}
Your lifetime cap is the package cap (${formatCurrency(LIFETIME_CAPS.nwop)}, indexed each March and September), and fees already paid under your package count towards it.</p>
</div>`
        : '';

    // ── Executive summary: what we recommend, and the benefit against the current position ──────
    const deltaLine = (label: string, delta: number, goodWhenPositive = true): string => {
        if (Math.round(delta) === 0) return `<li><strong>${label}</strong>: unchanged.</li>`;
        const better = goodWhenPositive ? delta > 0 : delta < 0;
        return `<li><strong>${label}</strong>: ${formatCurrency(Math.abs(delta))}/yr ${delta > 0 ? 'higher' : 'lower'}${better ? '' : ' — a cost of this strategy, weighed in its Risks section'}.</li>`;
    };
    const baselineLegacyValue = snapshot.baseline.legacy ?? null;
    const legacyDelta = baselineLegacyValue !== null ? snapshot.projectedLegacy - baselineLegacyValue : null;
    const recommendationSummaryBlock = `<div class="callout callout--blue">
<p class="callout-title">What we recommend — and what it does for you</p>
<p>${snapshot.narrativeTypes.length > 0
        ? `We recommend <strong>${snapshot.selectedStrategyName}</strong>. ${SAH_STRATEGY_EXPLAINERS[snapshot.narrativeTypes[0]]?.what || ''}`
        : `The strongest course for your position is <strong>${snapshot.selectedStrategyName}</strong> — keeping your current arrangements unchanged.`}</p>
<p style="margin-bottom:6px">Compared with your current position (doing nothing):</p>
<ul class="riskList" style="margin-bottom:0">
${deltaLine('Age Pension', snapshot.annualPension - snapshot.baseline.pension)}
${deltaLine('Support at Home contribution', snapshot.annualContribution - (snapshot.baseline.contribution || 0), false)}
${deltaLine('Yearly cash position', snapshot.annualCashflow - snapshot.baseline.cashflow)}
${legacyDelta !== null
        ? (Math.round(legacyDelta) === 0
            ? '<li><strong>Projected estate</strong>: unchanged.</li>'
            : `<li><strong>Projected estate</strong>: ${formatCurrency(Math.abs(legacyDelta))} ${legacyDelta > 0 ? 'higher' : 'lower'} over the projection${legacyDelta < 0 ? ' — the price of the benefits above, weighed in the Risks section' : ''}.</li>`)
        : ''}
</ul>
</div>`;

    // ── Contribution workings: the same three-step arithmetic the contributions screen teaches ──
    // Defensive against snapshots persisted before these fields existed (soaSnapshot on a saved
    // strategy is reopened as-generated).
    const th = snapshot.thresholds || { incomeMin: 0, incomeMax: 0, assetMin: 0, assetMax: 0 };
    const baselineRates = snapshot.baseline.contributionRates || { clinical: 0, independence: 0, everydayLiving: 0 };
    const workingsAssets = snapshot.afterStrategy?.assessableAssets || snapshot.baseline.assessableAssets;
    const workingsIncome = snapshot.afterStrategy?.assessableIncome || snapshot.baseline.assessableIncome;
    const bandPct = (value: number, min: number, max: number): number =>
        max > min ? Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100)) : 0;
    const incomePosPct = bandPct(workingsIncome, th.incomeMin, th.incomeMax);
    const assetPosPct = bandPct(workingsAssets, th.assetMin, th.assetMax);
    const assetsBind = assetPosPct >= incomePosPct;
    const winnerPct = assetsBind ? assetPosPct : incomePosPct;
    // Only show the band formula when it reproduces the displayed rate (standard part-pensioner
    // bands) — same self-check as the contributions screen.
    const bandsReproduce = Math.abs((0.05 + (winnerPct / 100) * 0.45) - snapshot.contributionRates.independence) < 0.0015
        && Math.abs((0.175 + (winnerPct / 100) * 0.625) - snapshot.contributionRates.everydayLiving) < 0.0015;
    const ratesChangedByStrategy = Math.abs(snapshot.contributionRates.independence - baselineRates.independence) > 0.0005
        || Math.abs(snapshot.contributionRates.everydayLiving - baselineRates.everydayLiving) > 0.0005;
    const contributionWorkingsBlock = `
<p class="small" style="margin-top:12px"><strong>These rates are AFTER the recommended strategy is applied.</strong>${ratesChangedByStrategy
        ? ` Before it, your modelled rates were ${pct(baselineRates.independence)} Independence and ${pct(baselineRates.everydayLiving)} Everyday Living — the strategy changes your means, and your rates with them.`
        : ' The strategy does not change your contribution rates.'}</p>
<details><summary><strong>Show the workings</strong></summary>
<div style="padding-top:8px">
${snapshot.rateOverride
        ? `<p class="small">Your rates are pinned to your official Services Australia assessment (see the disclosure above), so no sliding-scale arithmetic applies. The workings below show how the tool would otherwise model them.</p>`
        : ''}
${snapshot.noWorseOff
        ? noWorseOffWorkings(snapshot, workingsIncome, th.incomeMin, th.incomeMax)
        : `<p class="small"><strong>Step 1 — where you sit.</strong> Your assessable income of <strong>${formatCurrency(workingsIncome)}</strong> is ${incomePosPct.toFixed(1)}% of the way between the full-pension threshold (${formatCurrency(th.incomeMin)}) and the self-funded threshold (${formatCurrency(th.incomeMax)}). Your assessable assets of <strong>${formatCurrency(workingsAssets)}</strong> are ${assetPosPct.toFixed(1)}% of the way between ${formatCurrency(th.assetMin)} and ${formatCurrency(th.assetMax)}.</p>
<p class="small"><strong>Step 2 — which one counts.</strong> Whichever position is further along sets the rate — here, your <strong>${assetsBind ? 'assets' : 'income'}</strong> (${winnerPct.toFixed(1)}% of the way).</p>
${bandsReproduce
        ? `<p class="small"><strong>Step 3 — the arithmetic.</strong> Independence runs from 5% (full pensioner) to 50% (self-funded): 5% + ${winnerPct.toFixed(1)}% × 45% = <strong>${pct(snapshot.contributionRates.independence)}</strong>. Everyday Living runs from 17.5% to 80%: 17.5% + ${winnerPct.toFixed(1)}% × 62.5% = <strong>${pct(snapshot.contributionRates.everydayLiving)}</strong>.</p>`
        : `<p class="small"><strong>Step 3 — the result.</strong> Applying your position to your contribution bands gives <strong>${pct(snapshot.contributionRates.independence)}</strong> for Independence and <strong>${pct(snapshot.contributionRates.everydayLiving)}</strong> for Everyday Living.</p>`}
<p class="small muted">Clinical Care is always ${pct(snapshot.contributionRates.clinical)} — fully government funded at every means level.</p>`}
</div>
</details>`;

    const narrativeBlocks = snapshot.narrativeTypes.map((type) => {
        const title = SAH_STRATEGY_EXPLAINERS[type] ? type.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()) : type;
        return `<div class="h3">${title}</div>${narrativeSectionHtml(type)}`;
    }).join('');

    // ── Alternatives, structured like the aged care SOA: objective leaders first (strongest
    // projected estate / cashflow / Age Pension), the do-nothing baseline always present, and a
    // per-card drop-down for deeper analysis of each alternative. ──
    const alts = snapshot.alternatives;
    const doNothingBaseline = alts.find((a) => a.type === 'doNothing') || null;
    const bestBy = (metric: 'legacy' | 'cashflow' | 'pension') =>
        alts.length ? alts.reduce((best, a) => (a[metric] > best[metric] ? a : best), alts[0]) : null;
    const leaders: Array<{ alt: (typeof alts)[number]; objective: string }> = [];
    const addLeader = (alt: (typeof alts)[number] | null, objective: string) => {
        if (!alt) return;
        const existing = leaders.find((l) => l.alt.name === alt.name);
        if (existing) existing.objective += ` · ${objective}`;
        else leaders.push({ alt, objective });
    };
    addLeader(bestBy('legacy'), 'Strongest projected estate');
    addLeader(bestBy('cashflow'), 'Strongest cashflow');
    addLeader(bestBy('pension'), 'Strongest Age Pension');
    addLeader(doNothingBaseline, 'Do-nothing baseline');
    const selectedAlt = alts.find((a) => a.selected) || null;
    addLeader(selectedAlt, 'Your selection');
    const remaining = alts.filter((a) => !leaders.some((l) => l.alt.name === a.name)).slice(0, 6);

    const renderAltCard = (alt: (typeof alts)[number], objective: string) => {
        const explainer = SAH_STRATEGY_EXPLAINERS[alt.type];
        const dn = doNothingBaseline;
        const deltas = dn && alt.name !== dn.name
            ? `<li><strong>Projected estate:</strong> ${formatCurrency(Math.abs(alt.legacy - dn.legacy))} ${alt.legacy >= dn.legacy ? 'higher' : 'lower'} than doing nothing</li>
<li><strong>Cashflow:</strong> ${formatCurrency(Math.abs(alt.cashflow - dn.cashflow))}/yr ${alt.cashflow >= dn.cashflow ? 'better' : 'worse'}</li>
<li><strong>Age Pension:</strong> ${Math.round(alt.pension - dn.pension) === 0 ? 'unchanged' : `${formatCurrency(Math.abs(alt.pension - dn.pension))}/yr ${alt.pension >= dn.pension ? 'higher' : 'lower'}`}</li>`
            : '<li>This is the benchmark every other option is measured against.</li>';
        return `<div class="opt-card${alt.selected ? ' opt-card--selected' : ''}">
<div class="opt-card-head"><span class="opt-tag">${objective}</span>
<span class="opt-card-status">${alt.selected ? '<span class="pill-selected">Your selection</span>' : '<span class="pill-not-selected">Shown for comparison — not selected</span>'}</span></div>
<p class="opt-title">${alt.name}</p>
<div class="opt-card-main"><p class="opt-narr">${explainer?.what || ''}</p>
<div class="opt-metrics">
<div class="opt-metric-line"><span class="muted small">Projected estate</span><strong>${formatCurrency(alt.legacy)}</strong></div>
<div class="opt-metric-line"><span class="muted small">Cashflow / yr</span><strong>${formatCurrency(alt.cashflow)}</strong></div>
<div class="opt-metric-line"><span class="muted small">Age Pension / yr</span><strong>${formatCurrency(alt.pension)}</strong></div>
</div></div>
<details class="opt-funding"><summary>More about this strategy</summary>
<ul class="tip-ul">${deltas}</ul>
${explainer ? `<ul class="tip-ul">${explainer.consider.map((c) => `<li>${c}</li>`).join('')}</ul>` : ''}
</details>
</div>`;
    };

    const alternativesGrid = alts.length
        ? `<div class="opt-section">
<div class="opt-section-label">Objective comparison — strongest option per goal, and the baseline</div>
<div class="opt-grid">${leaders.map((l) => renderAltCard(l.alt, l.objective)).join('')}</div>
</div>
${remaining.length ? `<div class="opt-section">
<div class="opt-section-label">Also evaluated</div>
<div class="opt-grid">${remaining.map((a) => renderAltCard(a, a.type || 'strategy')).join('')}</div>
</div>` : ''}`
        : '<p class="muted">This document was generated from a manually constructed strategy; the suggestion engine\'s comparison set was not part of this run. The do-nothing baseline above remains the benchmark.</p>';

    const projectionRows = snapshot.projections.map((row, i) => `<tr class="proj-tr-data${i % 2 ? ' proj-zebra' : ''}">
<td class="proj-year">${row.label}</td>
<td class="td-num">${formatCurrency(row.pension)}</td>
<td class="td-num">${formatCurrency(row.sahCost)}</td>
<td class="td-num${row.cashflow >= 0 ? ' age-pos' : ' age-neg'}">${formatCurrency(row.cashflow)}</td>
<td class="td-num">${formatCurrency(row.netWorth)}</td>
</tr>`).join('');

    return `<!doctype html><html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>Statement of Advice - ${snapshot.clientName}</title>
<style>
:root{--ink:#0f172a;--muted:#64748b;--line:#e2e8f0;--card:#fff;--accent:#2563eb;--accent2:#4f46e5;--warn-bg:#fff7ed;--warn-border:#fdba74;--warn-ink:#9a3412;--radius:16px;--shadow:0 14px 40px rgba(15,23,42,.08)}
body{margin:0;font-family:Inter,Segoe UI,system-ui,sans-serif;background:var(--ink);color:var(--ink);line-height:1.55}
.cover{min-height:100vh;display:flex;align-items:center;justify-content:center;position:relative;overflow:hidden}
.cover::before{content:'';position:absolute;inset:0;background-image:url('${coverA}');background-size:cover;background-position:center;opacity:.38;animation:moveA 28s ease-in-out infinite}
.cover::after{content:'';position:absolute;inset:0;background-image:url('${coverB}');background-size:cover;background-position:center;opacity:.22;animation:moveB 34s ease-in-out infinite}
.coverShade{position:absolute;inset:0;background:radial-gradient(120% 80% at 30% 20%,rgba(37,99,235,.45),rgba(15,23,42,.88))}
.coverBox{position:relative;z-index:1;max-width:1040px;width:88%;background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.35);border-radius:28px;padding:40px 36px;color:#fff;backdrop-filter:blur(8px);box-shadow:0 24px 80px rgba(0,0,0,.35)}
.coverKicker{font-size:12px;letter-spacing:.22em;text-transform:uppercase;opacity:.9}
.coverTitle{font-size:clamp(36px,6vw,58px);line-height:1.05;font-weight:900;margin:10px 0 8px}
.clientNames{font-size:clamp(22px,3.5vw,32px);font-weight:700}
.meta{display:flex;flex-wrap:wrap;gap:8px;margin-top:18px}
.meta span{font-size:11px;padding:8px 12px;border-radius:999px;background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.4)}
.openBtn{margin-top:26px;padding:14px 22px;border-radius:14px;border:none;font-weight:800;cursor:pointer;background:#fff;color:var(--ink);font-size:15px;box-shadow:0 8px 24px rgba(0,0,0,.2);transition:transform .15s ease}
.openBtn:hover{transform:translateY(-2px)}
.book{background:linear-gradient(180deg,#eef2ff 0%,#f8fafc 18%);max-width:1200px;margin:0 auto;padding:28px 20px 48px;scroll-margin-top:0;min-height:100vh}
#soa-document{scroll-margin-top:0}
.topNav{position:sticky;top:0;background:rgba(255,255,255,.92);border:1px solid var(--line);border-radius:14px;padding:10px 12px;display:flex;justify-content:space-between;align-items:center;z-index:40;backdrop-filter:blur(10px);box-shadow:var(--shadow);margin-bottom:18px}
.topNav .buttons{display:flex;gap:8px;flex-wrap:wrap}
.topNav button{padding:9px 14px;min-width:90px;border:1px solid #c7d2fe;background:linear-gradient(180deg,#fff,#f1f5ff);border-radius:10px;cursor:pointer;font-weight:700;font-size:13px;color:var(--ink)}
.topNav button:hover{border-color:var(--accent);color:var(--accent)}
.pager{font-size:12px;font-weight:700;color:var(--muted)}
.page{display:none;animation:fadeIn .35s ease}
.page.active{display:block}
@keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
.card{background:var(--card);border:1px solid var(--line);border-radius:var(--radius);padding:26px 24px;margin-top:14px;box-shadow:var(--shadow);position:relative;overflow:visible}
.card::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg,#6366f1,#3b82f6);opacity:.9}
.h2{font-size:26px;font-weight:900;margin:0 0 12px;letter-spacing:-.02em}
.h3{font-size:17px;font-weight:800;margin:22px 0 10px;color:#1e293b}
.lede{font-size:16px;margin:10px 0 14px}
.muted{color:var(--muted)}.small{font-size:13px}.tiny{font-size:12px}
.goalsList{margin:8px 0 16px;padding-left:20px}
.goalsList li{margin:6px 0}
.grid4{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px}
.kpi{background:linear-gradient(180deg,#f8fafc,#fff);border:1px solid var(--line);border-radius:12px;padding:14px 12px}
.kpi--warn{background:var(--warn-bg);border-color:var(--warn-border)}
.kpi--warn .value{color:var(--warn-ink)}
.kpi--gain{background:#ecfdf5;border-color:#a7f3d0}
.kpi--gain .value{color:#047857}
.label{font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:.06em;font-weight:700}
.value{font-size:24px;font-weight:900;margin-top:4px}
.callout{border-radius:14px;padding:16px 18px;margin:16px 0}
.callout--blue{background:#eff6ff;border:1px solid #bfdbfe}
.callout--amber{background:#fffbeb;border:1px solid #fcd34d}
.callout--slate{background:#f8fafc;border:1px solid #e2e8f0}
.callout-title{font-weight:800;margin:0 0 8px;font-size:15px}
.card-inset{background:#fff;border:1px dashed #cbd5e1;border-radius:12px;padding:14px 16px;margin:14px 0}
.td-num{vertical-align:top;min-width:120px;white-space:nowrap}
.proj-year{white-space:nowrap;font-weight:700;color:#1f2937}
.age-pos{color:#16a34a}
.age-neg{color:#dc2626}
.table-proj{font-variant-numeric:tabular-nums}
.table-proj tbody tr.proj-tr-data.proj-zebra{background:#fafafa}
.section-ribbon{height:4px;border-radius:99px;background:linear-gradient(90deg,#4f46e5,#2563eb,#06b6d4);margin-bottom:16px}
.scope-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:12px}
.scope-in{background:#ecfdf5;border:1px solid #6ee7b7;border-radius:12px;padding:14px}
.scope-out{background:#fef2f2;border:1px solid #fecaca;border-radius:12px;padding:14px}
.scope-in h4,.scope-out h4{margin:0 0 8px;font-size:13px;text-transform:uppercase;letter-spacing:.06em}
.scope-in ul,.scope-out ul{margin:0;padding-left:18px;font-size:14px}
.opt-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px;margin-top:14px;align-items:stretch}
.opt-grid>.opt-card{height:100%}
.opt-card{border:1px solid var(--line);border-radius:14px;padding:16px;background:#fafafa;transition:box-shadow .2s,border-color .2s,transform .2s;display:flex;flex-direction:column;min-height:100%;box-sizing:border-box}
.opt-card:hover{box-shadow:var(--shadow);transform:translateY(-2px)}
.opt-card--selected{border-color:#818cf8;background:linear-gradient(160deg,#eef2ff,#fff);box-shadow:0 12px 36px rgba(79,70,229,.15)}
.opt-card-head{display:flex;flex-direction:column;align-items:flex-start;gap:8px;margin-bottom:8px;width:100%}
.opt-card-head .opt-tag{display:inline-block;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.08em;color:var(--accent);background:#eff6ff;padding:4px 8px;border-radius:999px;max-width:100%}
.opt-card-status{display:block;width:100%}
.pill-not-selected{font-size:10px;font-weight:800;background:#e2e8f0;color:#475569;padding:4px 10px;border-radius:999px;letter-spacing:.02em}
.opt-title{font-size:16px;margin:0 0 10px;font-weight:800;line-height:1.25}
.pill-selected{font-size:10px;font-weight:800;background:#22c55e;color:#fff;padding:4px 10px;border-radius:999px;letter-spacing:.02em}
.opt-card-main{flex:1 1 auto;display:flex;flex-direction:column;min-height:0}
.opt-narr{font-size:14px;color:#475569;margin:0 0 0;flex:1 1 auto;line-height:1.45}
.opt-metrics{margin-top:auto;padding-top:14px;border-top:1px solid #e2e8f0;flex-shrink:0;display:grid;grid-template-columns:1fr;gap:8px;font-size:14px}
.opt-metric-line{display:flex;justify-content:space-between;align-items:baseline;gap:12px}
.opt-metric-line strong{font-size:18px;line-height:1.2;white-space:nowrap}
.opt-section{margin-top:6px}
.opt-section-label{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.07em;color:#64748b;margin:18px 0 10px}
.opt-grid--2{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px;margin-top:10px;align-items:stretch}
.opt-funding{margin-top:10px;flex-shrink:0}
.opt-funding summary{cursor:pointer;font-weight:700;color:#334155}
.tip-ul{margin:8px 0 2px 16px;padding:0;color:#334155;line-height:1.45;max-height:220px;overflow:auto}
.riskList{margin:0;padding-left:20px}
.riskList li{margin:10px 0}
table{width:100%;border-collapse:collapse;font-size:14px}
th,td{padding:11px 10px;border-bottom:1px solid var(--line);text-align:left}
th{background:#f1f5f9;font-weight:800;font-size:12px;text-transform:uppercase;letter-spacing:.04em;color:#475569}
details{margin:10px 0;border:1px solid var(--line);border-radius:12px;padding:12px;background:#fafafa}
.factSection{margin-top:8px}.factRow{display:grid;grid-template-columns:minmax(0,320px) 1fr;padding:10px 8px;border-bottom:1px solid #f1f5f9;gap:12px}.factLabel{font-weight:700;color:#334155;font-size:13px}
@keyframes moveA{0%,100%{transform:scale(1) translate(0,0)}50%{transform:scale(1.09) translate(-14px,-12px)}}
@keyframes moveB{0%,100%{transform:scale(1) translate(0,0)}50%{transform:scale(1.06) translate(16px,14px)}}
@media (max-width:1020px){.grid4{grid-template-columns:repeat(2,minmax(0,1fr))}.opt-grid{grid-template-columns:1fr}.scope-grid{grid-template-columns:1fr}}
@media (max-width:640px){.grid4{grid-template-columns:1fr}.factRow{grid-template-columns:1fr}}
@media print{
  @page{size:A4 portrait;margin:12mm}
  body{background:#fff !important;color:#000 !important}
  .cover{min-height:auto !important;page-break-after:always}
  .cover::before,.cover::after,.coverShade{display:none !important}
  .coverBox{background:#fff !important;color:#000 !important;border:1px solid #e5e7eb !important;box-shadow:none !important;backdrop-filter:none !important}
  .openBtn{display:none !important}
  .book{max-width:none !important;margin:0 !important;padding:0 !important;background:#fff !important}
  .topNav,.pager,.buttons{display:none !important}
  .page,.page.active{display:block !important;animation:none !important;break-inside:avoid-page;page-break-inside:avoid;margin:0 0 10mm 0}
  .card{box-shadow:none !important;border:1px solid #d1d5db !important;break-inside:avoid-page;page-break-inside:avoid;background:#fff !important}
  details{border:1px solid #d1d5db !important;background:#fff !important}
  table{font-size:11px !important}
  th,td{padding:6px 6px !important}
}
${SOA_THEME_CSS}
</style></head><body>
<section class="cover"><div class="coverShade"></div><div class="coverBox">
<div class="coverKicker">Statement of advice</div>
<div class="coverTitle">Your Support at Home strategy</div>
<div class="clientNames">${snapshot.clientName}${snapshot.partnerName ? ` & ${snapshot.partnerName}` : ''}</div>
<div class="meta">
${(() => {
    const a = getCurrentAdviceAttribution();
    // Same rule as the aged-care document: the firm's own attribution when we know it, and the
    // placeholder that says "(Demo)" out loud when we do not.
    return a
        ? `<span>${String(a.line).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</span>`
        : '<span>AgedCareWise Pty Ltd</span><span>AFSL 401234 (Demo)</span><span>ABN 12 345 678 901 (Demo)</span>';
})()}
<span>Report ${snapshot.reportId}</span><span>${formatDateOnly(snapshot.generatedAt)}</span>
<span>Source: ${snapshot.strategySource === 'manual_control' ? 'MC (Manual Control)' : 'SE (Suggestion Engine)'}</span>
${snapshot.completerDisplay ? `<span>Completed by: ${snapshot.completerDisplay}</span>` : ''}
</div>
<button type="button" class="openBtn" onclick="openBooklet()">Begin reading your advice</button>
<p class="muted" style="margin-top:14px;font-size:13px;opacity:.85">Opens your Statement of Advice below — use <strong>Next</strong> / <strong>Previous</strong> to move through each section.</p>
</div></section>
<div class="book" id="soa-document">
<div class="topNav">
  <div class="buttons">
    <button type="button" onclick="prevPage()">Previous</button>
    <button type="button" onclick="nextPage()">Next</button>
    <button type="button" data-goto="0">Contents</button>
  </div>
  <div class="pager" id="pager"></div>
</div>

<section class="page active"><div class="card">
<div class="h2">Executive summary</div>
<p class="lede">This Statement of Advice covers strategies to fund <strong>Support at Home</strong> services — care delivered in your own home — and their effect on your Age Pension, your contribution rates, your cash flow and your estate.</p>
<p class="lede">${snapshot.strategySource === 'manual_control'
        ? `You selected <strong>${snapshot.selectedStrategyName}</strong> as the preferred manually constructed strategy. This document follows that strategy through to projected outcomes.`
        : `We evaluated <strong>${snapshot.totalEvaluated}</strong> candidate strategies against your actual position. You selected <strong>${snapshot.selectedStrategyName}</strong>, and this document follows that strategy through to projected outcomes.`}</p>
<div class="h3">What you told us you are trying to achieve</div>
${goalsBlock}
<div class="h3">Snapshot of modelled outcomes</div>
<div class="grid4">
<div class="kpi"><div class="label">Age Pension / year</div><div class="value">${formatCurrency(snapshot.annualPension)}</div></div>
<div class="kpi"><div class="label">Support at Home contribution / year</div><div class="value">${formatCurrency(snapshot.annualContribution)}</div></div>
<div class="${cashflowKpiClass}"><div class="label">Cashflow / year</div><div class="value">${formatCurrency(snapshot.annualCashflow)}</div></div>
<div class="kpi"><div class="label">Projected estate</div><div class="value">${formatCurrency(snapshot.projectedLegacy)}</div></div>
</div>
${recommendationSummaryBlock}
${snapshot.agedCareLinked ? `<div class="callout callout--blue">
<p class="callout-title">Linked aged care scenario</p>
<p class="small">This analysis was run with a linked aged care scenario — one partner is modelled as entering residential care, so the household figures include that scenario's accommodation and care costs, and Centrelink's <strong>illness-separated couple</strong> Age Pension treatment may apply. Services Australia determines the actual treatment; inform them when residential care commences.</p>
</div>` : ''}
${overrideBlock}${noWorseOffBlock}
<div class="h3">In this document</div>
<div id="soa-toc-slot"></div>
</div></section>

<section class="page"><div class="card">
<div class="h2">Scope of advice</div>
<p>This section sets clear boundaries: what this advice covers, what it does not, and where you still need specialist input.</p>
<div class="scope-grid">
<div class="scope-in">
<h4>In scope</h4>
<ul>
<li>Strategies to fund <strong>Support at Home</strong> services and improve your position — asset restructuring, home-equity access and debt strategies — and how they interact with your Age Pension and contribution rates.</li>
<li>Illustrative <strong>cashflow, pension, contribution and net-worth projections</strong> — not forecasts of actual future balances.</li>
<li>Education on how Support at Home contribution rates are <strong>estimated</strong> in this tool (final rates are set by Services Australia's assessment).</li>
</ul>
</div>
<div class="scope-out">
<h4>Out of scope</h4>
<ul>
<li>Residential aged care advice (covered by a separate aged care Statement of Advice where applicable).</li>
<li>Legal documents (Wills, powers of attorney), tax return preparation, and product selection for specific annuity or reverse-mortgage providers.</li>
<li>Confirmation of government entitlements — Services Australia determines actual Age Pension and contribution outcomes.</li>
</ul>
</div>
</div>
</div></section>

${snapshot.situation ? `<section class="page"><div class="card">
<div class="h2">Your situation today</div>
<p class="lede">The position this advice starts from, as you entered it, before any strategy — and what is left to the family if nothing changes.</p>
${renderSituationHtml(snapshot.situation, { name: 'Doing nothing', pension: snapshot.baseline.pension, cashflow: snapshot.baseline.cashflow, estate: snapshot.baseline.legacy ?? snapshot.projectedLegacy }, snapshot.legacyYears ?? 5)}
</div></section>

` : ''}<section class="page"><div class="card">
<div class="h2">Your contribution assessment</div>
<p class="lede">Support at Home services are charged across three categories. Clinical care is fully government funded for everyone; your means determine what you contribute to the other two.</p>
<table><thead><tr><th>Category</th><th>Your rate</th><th>What it covers</th></tr></thead><tbody>
<tr><td>Clinical Care</td><td class="td-num">${pct(snapshot.contributionRates.clinical)}</td><td>Nursing, physiotherapy, personal care (fully funded from 1 Oct 2026)</td></tr>
<tr><td>Independence</td><td class="td-num">${pct(snapshot.contributionRates.independence)}</td><td>Social support, transport, respite</td></tr>
<tr><td>Everyday Living</td><td class="td-num">${pct(snapshot.contributionRates.everydayLiving)}</td><td>Cleaning, gardening, meals</td></tr>
</tbody></table>
${overrideBlock || `<p class="small muted" style="margin-top:12px">These rates are modelled from your means. Your real co-contribution is decided by Services Australia's assessment and may differ.</p>`}
${contributionWorkingsBlock}
</div></section>

<section class="page"><div class="card">
<div class="h2">Your service plan</div>
${snapshot.servicePlan.isEstimated
        ? `<div class="callout callout--amber"><p class="callout-title">Illustrative package</p><p>${snapshot.servicePlan.description || 'No service plan was selected, so the modelling used a documented illustrative service mix. Build your actual plan in the Service Builder for costs based on your assessed services.'}</p></div>`
        : `<p class="lede">The modelling in this document is costed against the following services (indicative gross costs at median catalogue prices — your out-of-pocket contribution applies your rates above).</p>`}
${servicePlanTableHtml(snapshot.clientName, snapshot.servicePlan.clientRows, Boolean(snapshot.partnerName))}
${servicePlanTableHtml(snapshot.partnerName || 'Partner', snapshot.servicePlan.partnerRows, Boolean(snapshot.partnerName))}
${snapshot.servicePlan.grossAnnual > 0 ? `<p class="small" style="margin-top:10px"><strong>Total gross services: ${formatCurrency(snapshot.servicePlan.grossAnnual)}/yr.</strong> Your modelled contribution is ${formatCurrency(snapshot.annualContribution)}/yr; the government funds the balance.</p>` : ''}
</div></section>

<section class="page"><div class="card">
<div class="h2">The recommended strategy</div>
<p class="lede"><strong>${snapshot.selectedStrategyName}</strong></p>
${narrativeBlocks || '<p class="muted">This strategy makes no changes to your position (do nothing) — it is the baseline the alternatives were measured against.</p>'}
${snapshot.strategyRisks?.sections?.length ? `<div class="h3">What could go wrong with this strategy</div>
<p class="muted">Every plan has trade-offs. These are the ones that matter for the strategy recommended here, as they were shown to you when you chose it.</p>
${snapshot.strategyRisks.sections.map((s: { title: string; points: { lead: string; body: string }[] }) => `<div class="h3" style="font-size:13px">${s.title}</div><ul class="riskList">${s.points.map((p) => `<li><strong>${p.lead}</strong> ${p.body}</li>`).join('')}</ul>`).join('')}` : ''}
<div class="callout callout--slate">
<p class="callout-title">Measured against doing nothing</p>
<p>Age Pension ${formatCurrency(snapshot.annualPension)}/yr (baseline ${formatCurrency(snapshot.baseline.pension)}/yr) · Cashflow ${formatCurrency(snapshot.annualCashflow)}/yr (baseline ${formatCurrency(snapshot.baseline.cashflow)}/yr) · Projected estate ${formatCurrency(snapshot.projectedLegacy)}.</p>
</div>
</div></section>

<section class="page"><div class="card">
<div class="h2">Alternative strategies considered</div>
<p class="lede">${snapshot.strategySource === 'manual_control'
        ? 'This strategy was constructed manually. The numbers above are measured against the do-nothing baseline.'
        : `The engine evaluated ${snapshot.totalEvaluated} strategies against your actual position. The leading options are shown below; the projected estate on each card is the comparison.`}</p>
${alternativesGrid}
</div></section>

<section class="page"><div class="card">
<div class="h2">Projection outlook</div>
<p class="lede">Year-by-year modelled outcomes for the selected strategy${(snapshot.baselineProjections || []).length ? ', against doing nothing' : ''}. ${snapshot.projectionConvention} Hover over a year for its figures.</p>
${renderProjectionCharts({
    idPrefix: 'sah-proj',
    planName: plainText(snapshot.selectedStrategyName),
    plan: snapshot.projections.map((r) => ({ label: plainText(r.label), pension: r.pension, cashflow: r.cashflow, netWorth: r.netWorth })),
    baselineName: 'Doing nothing',
    baseline: (snapshot.baselineProjections || []).map((r) => ({ ...r, label: plainText(r.label) })),
})}
<div class="h3" style="margin-top:18px">Year by year</div>
${snapshot.projections.length ? `<table class="table-proj"><thead><tr><th>Year</th><th>Age Pension</th><th>Support at Home costs</th><th>Cashflow</th><th>Net worth</th></tr></thead><tbody>${projectionRows}</tbody></table>` : '<p class="muted">Projection rows were not available when this document was generated.</p>'}
</div></section>

<section class="page"><div class="card">
<div class="h2">Fees &amp; disclosures</div>
<table><thead><tr><th>Item</th><th>Amount</th><th>Notes</th></tr></thead><tbody>
<tr><td>Advice fee (initial)</td><td>$450 + GST</td><td>No ongoing or implementation fee in this pathway</td></tr>
<tr><td>Commissions</td><td>None</td><td>AgedCareWise receives no commissions or product payments in relation to this advice</td></tr>
</tbody></table>
<div class="callout callout--slate">
<p class="callout-title">Important disclosures</p>
<p class="small">This document illustrates modelled outcomes using the assumptions in the appendix; it is not a guarantee of future outcomes. Support at Home contribution rates and Age Pension entitlements are determined by Services Australia — modelled figures are estimates until formally assessed. Where a strategy involves a financial product (annuity, reverse mortgage), the modelled outcome uses standard assumptions and the actual product's terms govern; obtain and compare the product's current disclosure documents before implementing.</p>
</div>
</div></section>

<section class="page"><div class="card">
<div class="h2">Risks &amp; limitations</div>
<ul class="riskList">
<li><strong>Modelling, not prophecy:</strong> projections use the assumptions in the appendix; actual investment returns, indexation and legislation will differ.</li>
<li><strong>Assessment risk:</strong> your contribution rates and Age Pension are determined by Services Australia. Modelled figures — and even officially assessed rates — change when your circumstances change.</li>
<li><strong>Strategy-specific risks:</strong> each recommended strategy's own risks are stated in its section above and form part of this advice.</li>
<li><strong>Liquidity:</strong> strategies that convert accessible savings into exempt or illiquid forms reduce your buffer for unexpected costs, including future care needs.</li>
<li><strong>Product terms:</strong> where a strategy involves a product (annuity, reverse mortgage), the modelled outcome uses standard assumptions; the actual product's terms govern.</li>
</ul>
${snapshot.ratesReviewNotice ? `<div class="callout callout--amber"><p class="callout-title">Rates review</p><p>${snapshot.ratesReviewNotice}</p></div>` : ''}
</div></section>

<section class="page"><div class="card">
<div class="h2">Next steps</div>
${pensionNextStepsHtml(snapshot.annualPension, snapshot.baseline.pension)}
<ul class="riskList">
<li>Review this document with your family or trusted adviser.</li>
<li>Confirm your Support at Home assessment and contribution rates with Services Australia; if you receive an assessment letter, your adviser can record the official rates in the tool.</li>
<li>Build or refine your service plan in the Service Builder so costs reflect your actual assessed services.</li>
<li>Before implementing any product-based strategy (annuity, reverse mortgage, HEAS), obtain the product's current terms and compare providers.</li>
<li>Revisit this advice when circumstances change materially — health, family, markets or legislation.</li>
</ul>
</div></section>

<section class="page"><div class="card">
<div class="h2">Appendix — fact-find &amp; assumptions</div>
<p><strong>Client age:</strong> ${snapshot.clientAge ?? 'Not provided'}${snapshot.partnerName ? ` | <strong>Partner:</strong> ${snapshot.partnerName}` : ''}</p>
<p><strong>Living expenses (annual):</strong> ${formatCurrency(snapshot.annualLivingExpenses || 0)}</p>
<div class="h3">Position at generation</div>
<div class="factSection">
<div class="factRow"><div class="factLabel">Assessable income (modelled)</div><div>${formatCurrency(snapshot.baseline.assessableIncome)}/yr</div></div>
<div class="factRow"><div class="factLabel">Assessable assets (modelled)</div><div>${formatCurrency(snapshot.baseline.assessableAssets)}</div></div>
<div class="factRow"><div class="factLabel">Age Pension before this strategy</div><div>${formatCurrency(snapshot.baseline.pension)}/yr</div></div>
<div class="factRow"><div class="factLabel">Cashflow before this strategy</div><div>${formatCurrency(snapshot.baseline.cashflow)}/yr</div></div>
</div>
<div class="h3">Asset assumptions</div>
<table><thead><tr><th>Asset type</th><th>Description</th><th>Value</th><th>Income %</th><th>Growth %</th><th>Client's own figures</th></tr></thead><tbody>
${(snapshot.assetRows || []).map((row) => `<tr><td>${row.assetType}</td><td>${row.description}${row.basis ? `<div class="tiny muted">${row.basis}</div>` : ''}</td><td>${formatCurrency(row.value)}</td><td>${row.incomeRate.toFixed(2)}%</td><td>${row.growthRate.toFixed(2)}%</td><td>${row.overridden ? 'Yes' : 'No'}</td></tr>`).join('')}
</tbody></table>
<p class="tiny muted">Income % is what the model applied. For an investment property it is the yield the recorded rent represents — the projections use the rent and expense dollars recorded for the property, not an assumed yield.</p>
${snapshot.structureAppendixHtml || ''}
<div class="h3">Structured fact-find responses</div>
${(snapshot.factFindSections || []).map((section) => `
<details class="factSection"><summary><strong>${section.title}</strong></summary>
  <div>
    ${section.rows.map((row) => `<div class="factRow"><div class="factLabel">${row.label}</div><div>${toDisplay(row.value)}</div></div>`).join('')}
  </div>
</details>`).join('')}
<details class="factSection"><summary><strong>Rates &amp; thresholds used</strong></summary>
<table><thead><tr><th>Rate / threshold</th><th>Value used</th><th>Notes</th></tr></thead><tbody>
<tr><td>Clinical Care contribution</td><td>0%</td><td>Fully government funded at every means level (personal care included from 1 Oct 2026)</td></tr>
${snapshot.noWorseOff
    ? `<tr><td>Independence contribution band ('no worse off')</td><td>${snapshot.noWorseOff.paidNoFee ? '0%' : '0% – 25%'}</td><td>${snapshot.noWorseOff.paidNoFee ? 'No income-tested care fee was payable under the package: no contributions' : 'Full pensioner 0%, self-funded 25%; otherwise on income only (Services Australia’s four-step method)'}</td></tr>
<tr><td>Everyday Living contribution band ('no worse off')</td><td>${snapshot.noWorseOff.paidNoFee ? '0%' : '0% – 25%'}</td><td>Same basis as Independence</td></tr>
<tr><td>Income band (income free area → income limit)</td><td>${formatCurrency(th.incomeMin)} – ${formatCurrency(th.incomeMax)}</td><td>The only thresholds the 'no worse off' rate uses; assets play no part</td></tr>
${snapshot.noWorseOff.previousFeeDaily > 0 ? `<tr><td>Fortnightly ceiling</td><td>${formatCurrency(snapshot.noWorseOff.previousFeeDaily * 14)}</td><td>The previous income-tested care fee (${formatCurrency(snapshot.noWorseOff.previousFeeDaily)} a day × 14); contributions never exceed it</td></tr>` : ''}
<tr><td>Lifetime cap</td><td>${formatCurrency(LIFETIME_CAPS.nwop)}</td><td>The package cap, indexed; fees paid under the package count towards it</td></tr>`
    : `<tr><td>Independence contribution band</td><td>5% – 50%</td><td>Full pensioner minimum to self-funded maximum; part pensioners slide between</td></tr>
<tr><td>Everyday Living contribution band</td><td>17.5% – 80%</td><td>Full pensioner minimum to self-funded maximum; part pensioners slide between</td></tr>
<tr><td>Income band (full-pension → self-funded)</td><td>${formatCurrency(th.incomeMin)} – ${formatCurrency(th.incomeMax)}</td><td>Assessable income thresholds used for the sliding scale</td></tr>
<tr><td>Asset band (full-pension → self-funded)</td><td>${formatCurrency(th.assetMin)} – ${formatCurrency(th.assetMax)}</td><td>Assessable asset thresholds used for the sliding scale</td></tr>`}
<tr><td>Service prices</td><td>Median catalogue prices</td><td>Gross service costs; providers' actual prices vary</td></tr>
<tr><td>Deeming</td><td>Current deeming rates &amp; thresholds</td><td>Applied to financial assets for the income test</td></tr>
<tr><td>Pension transfer balance cap</td><td>${formatCurrency(GENERAL_TRANSFER_BALANCE_CAP)} per person (lifetime)</td><td>Where a strategy commences a pension, only the person's remaining cap is moved into it; any excess goes to accumulation super. Cap usage uses the ATO transfer balance account figure where recorded, otherwise it is estimated from current pension balances — an estimate that can understate usage where a pension was commenced years ago and since drawn down.</td></tr>
<tr><td>Advice fee (initial)</td><td>$450 + GST</td><td>No ongoing or implementation fee in this pathway</td></tr>
</tbody></table>
</details>
<div class="h3">Tax, super, Age Pension, Support at Home and market assumptions</div>
<p class="small muted">Every rate and threshold the model used, as the Assumptions page showed them when this document was generated. Open a group to read it.</p>
${renderAssumptionsAppendixHtml(snapshot.assumptionSections || buildAssumptionSections(), /Support at Home|Social Security|Tax/i)}
<div class="h3">Conventions</div>
<p class="small">${snapshot.projectionConvention} Service costs use median catalogue prices; contribution dollars apply your modelled (or officially recorded) contribution rates.</p>
<p class="tiny muted" style="margin-top:16px">Report ${snapshot.reportId} · Generated ${formatDateOnly(snapshot.generatedAt)} · Source: ${snapshot.strategySource === 'manual_control' ? 'Manual Control' : 'Suggestion Engine'} · This document is a modelling illustration prepared by AgedCareWise and does not replace formal assessment by Services Australia.</p>
</div></section>

</div>
<script src="${soaBookletScriptUrl}"></script>
</body></html>`;
}

// ── open / download ─────────────────────────────────────────────────────────────────────────────

export function openSupportAtHomeDigitalSOAAttachment(snapshot: SupportAtHomeSoaSnapshot | null | undefined): void {
    if (!snapshot) return;
    const html = buildHtml(snapshot);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const safeName = snapshot.clientName.replace(/[^a-zA-Z0-9]/g, '_');

    const tab = window.open('', '_blank');
    if (tab) {
        tab.document.open();
        tab.document.write(html);
        tab.document.close();
    } else {
        const openLink = document.createElement('a');
        openLink.href = url;
        openLink.target = '_blank';
        openLink.rel = 'noopener noreferrer';
        document.body.appendChild(openLink);
        openLink.click();
        document.body.removeChild(openLink);
    }

    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = `Digital_SOA_SupportAtHome_${safeName}_${snapshot.generatedAt.slice(0, 10)}.html`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    setTimeout(() => URL.revokeObjectURL(url), 30000);
}

/** Claiming, or reporting, the pension change the plan rests on (owner, 3 Oct 2026): a call-out above the next steps. */
function pensionNextStepsHtml(planPension: number, baselinePension: number): string {
    const note = pensionCallout(planPension, baselinePension, { meansAssessment: 'home' });
    if (!note) return '';
    const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return `<div class="callout callout--amber"><p class="callout-title">${esc(note.implication.title)}</p><p><strong>${esc(note.implication.lead)}</strong> ${esc(note.implication.body)}</p><p><strong>Next step:</strong> ${esc(note.nextStep)}</p></div>`;
}
